import React, { useState, useEffect } from 'react';
import { FaTimes, FaSave, FaInfoCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { toast } from 'react-toastify';
import EmailTemplateService, {
  EmailTemplateType,
  EmailTemplate,
  AVAILABLE_PLACEHOLDERS,
} from '../services/EmailTemplateService';

interface EmailTemplateModalProps {
  type: EmailTemplateType;
  onClose: () => void;
  onSave: () => void;
}

const DEFAULT_TEMPLATES: Record<EmailTemplateType, { subject: string; body: string }> = {
  OFFER: {
    subject: 'Angebot {{ offer.offer_number }}: {{ offer.title }}',
    body: `{{ partner.salutation }},

anbei erhalten Sie unser Angebot {{ offer.offer_number }} mit dem Titel "{{ offer.title }}".

Gesamtbetrag: {{ offer.total_gross }} (inkl. MwSt.)
Gültig bis: {{ offer.valid_until }}

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  ORDER_CONFIRMATION: {
    subject: 'Auftragsbestätigung {{ offer.order_confirmation_number }}',
    body: `{{ partner.salutation }},

vielen Dank für Ihren Auftrag. Anbei erhalten Sie unsere Auftragsbestätigung {{ offer.order_confirmation_number }}.

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  INVOICE: {
    subject: 'Rechnung {{ invoice.invoice_number }}',
    body: `{{ partner.salutation }},

anbei erhalten Sie unsere Rechnung {{ invoice.invoice_number }}.

Rechnungsbetrag: {{ invoice.total_gross }}
Zahlungsziel: {{ invoice.due_date }}

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  REMINDER: {
    subject: 'Zahlungserinnerung: Rechnung {{ invoice.invoice_number }}',
    body: `{{ partner.salutation }},

wir möchten Sie freundlich daran erinnern, dass die Rechnung {{ invoice.invoice_number }} über {{ invoice.total_gross }} am {{ invoice.due_date }} fällig war.

Sollte die Zahlung bereits erfolgt sein, betrachten Sie dieses Schreiben bitte als gegenstandslos.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  DUNNING_1: {
    subject: '1. Mahnung: Rechnung {{ invoice.invoice_number }}',
    body: `{{ partner.salutation }},

leider konnten wir bis heute keinen Zahlungseingang für die Rechnung {{ invoice.invoice_number }} feststellen.

Rechnungsbetrag: {{ invoice.total_gross }}
Fällig seit: {{ invoice.days_overdue }} Tagen

Wir bitten Sie, den Betrag innerhalb der nächsten 7 Tage zu überweisen.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  DUNNING_2: {
    subject: '2. Mahnung: Rechnung {{ invoice.invoice_number }}',
    body: `{{ partner.salutation }},

trotz unserer ersten Mahnung ist die Rechnung {{ invoice.invoice_number }} noch immer nicht beglichen.

Rechnungsbetrag: {{ invoice.total_gross }}
Fällig seit: {{ invoice.days_overdue }} Tagen

Wir bitten Sie dringend, den Betrag innerhalb von 5 Tagen zu überweisen, um weitere Maßnahmen zu vermeiden.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
  DUNNING_3: {
    subject: 'Letzte Mahnung vor gerichtlichem Mahnverfahren: Rechnung {{ invoice.invoice_number }}',
    body: `{{ partner.salutation }},

dies ist unsere letzte Mahnung bezüglich der offenen Rechnung {{ invoice.invoice_number }}.

Rechnungsbetrag: {{ invoice.total_gross }}
Fällig seit: {{ invoice.days_overdue }} Tagen

Sollte der Betrag nicht innerhalb von 3 Tagen bei uns eingehen, sehen wir uns gezwungen, ein gerichtliches Mahnverfahren einzuleiten.

Mit freundlichen Grüßen
{{ tenant.company_name }}`,
  },
};

export default function EmailTemplateModal({
  type,
  onClose,
  onSave,
}: EmailTemplateModalProps): React.JSX.Element {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [template, setTemplate] = useState<EmailTemplate | null>(null);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [showPlaceholders, setShowPlaceholders] = useState(false);

  useEffect(() => {
    loadTemplate();
  }, [type]);

  const loadTemplate = async () => {
    setLoading(true);
    try {
      const data = await EmailTemplateService.getByType(type);
      setTemplate(data);
      setSubject(data.subject_template);
      setBodyHtml(data.body_html_template);
      setIsActive(data.is_active);
    } catch (err) {
      // Template doesn't exist yet, use defaults
      const defaults = DEFAULT_TEMPLATES[type];
      setSubject(defaults.subject);
      setBodyHtml(defaults.body);
      setTemplate(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!subject.trim()) {
      toast.error('Betreff ist erforderlich');
      return;
    }

    if (!bodyHtml.trim()) {
      toast.error('Nachrichtentext ist erforderlich');
      return;
    }

    setSaving(true);
    try {
      if (template?.id) {
        await EmailTemplateService.update(template.id, {
          subject_template: subject,
          body_html_template: bodyHtml,
          is_active: isActive,
        });
      } else {
        await EmailTemplateService.create({
          type,
          subject_template: subject,
          body_html_template: bodyHtml,
          is_active: isActive,
        });
      }
      toast.success('Template gespeichert');
      onSave();
    } catch (err) {
      console.error('Failed to save template:', err);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (placeholder: string) => {
    const textarea = document.getElementById('body-textarea') as HTMLTextAreaElement;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = bodyHtml.substring(0, start) + placeholder + bodyHtml.substring(end);
      setBodyHtml(newValue);
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + placeholder.length, start + placeholder.length);
      }, 0);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            Template bearbeiten: {EmailTemplateService.getTypeLabel(type)}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Loading...
            </div>
          ) : (
            <>
              {/* Subject */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Betreff <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="E-Mail-Betreff mit Platzhaltern..."
                  style={inputStyle}
                />
              </div>

              {/* Body */}
              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>
                  Nachricht <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <textarea
                  id="body-textarea"
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  placeholder="E-Mail-Text mit Platzhaltern..."
                  rows={12}
                  style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: '13px' }}
                />
              </div>

              {/* Active Toggle */}
              <div style={{ marginBottom: '20px' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    style={{ width: '18px', height: '18px', accentColor: '#7c3aed' }}
                  />
                  <span>Template aktiv</span>
                </label>
              </div>

              {/* Placeholders Accordion */}
              <div
                style={{
                  border: '1px solid #e0e3ef',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <button
                  onClick={() => setShowPlaceholders(!showPlaceholders)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: '#f8f9fc',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#374151',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FaInfoCircle size={14} color="#7c3aed" />
                    Verfügbare Platzhalter
                  </span>
                  {showPlaceholders ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                </button>

                {showPlaceholders && (
                  <div style={{ padding: '16px' }}>
                    {AVAILABLE_PLACEHOLDERS.map((category) => (
                      <div key={category.category} style={{ marginBottom: '16px' }}>
                        <div
                          style={{
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#666',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            marginBottom: '8px',
                          }}
                        >
                          {category.category}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {category.placeholders.map((ph) => (
                            <button
                              key={ph.key}
                              onClick={() => insertPlaceholder(ph.key)}
                              title={ph.description}
                              style={{
                                padding: '4px 10px',
                                background: '#e0e7ff',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: '12px',
                                fontFamily: 'monospace',
                                color: '#3730a3',
                                cursor: 'pointer',
                              }}
                            >
                              {ph.key}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            padding: '16px 24px',
            borderTop: '1px solid #e0e3ef',
            background: '#f8f9fc',
          }}
        >
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              border: '1px solid #e0e3ef',
              borderRadius: '10px',
              background: 'white',
              color: '#666',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 24px',
              border: 'none',
              borderRadius: '10px',
              background: saving || loading ? '#ccc' : '#021a4a',
              color: 'white',
              fontSize: '14px',
              fontWeight: 600,
              cursor: saving || loading ? 'not-allowed' : 'pointer',
            }}
          >
            <FaSave size={14} />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};
